import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import { BudgetEntryPostModel } from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

export interface AssignmentBudgetFilters {
    assignmentId?: number;
    catId?: number;
    staffId?: number;
}

interface Props {
    id?: number;
    filters?: AssignmentBudgetFilters;
}

export default function useBudgetData({ id, filters }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const entryDetailsQuery = useQuery({
        queryKey: [QueryKeys.GetBudget, { id }],
        queryFn: () => apis.engine.assignment.assignmentBudgetEntryDetail(id!),
        enabled: id != undefined,
    });

    const entriesSearchListQuery = useQuery({
        queryKey: [QueryKeys.BudgetSearch, filters],
        queryFn: () => apis.engine.assignment.assignmentBudgetEntrySearchList(filters),
        enabled: filters != undefined,
    });

    const entryTotalsQuery = useQuery({
        queryKey: [QueryKeys.BudgetTotals, filters],
        queryFn: () => apis.engine.assignment.assignmentBudgetEntrySearchTotalsList(filters),
        enabled: filters != undefined,
    });

    const saveEntryMutation = useMutation({
        mutationFn: (post: BudgetEntryPostModel) => apis.engine.assignment.assignmentBudgetEntrySaveCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.BudgetSearch] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.BudgetTotals] });

            toast.success("Entry saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save entry"),
    });

    const deleteEntryMutation = useMutation({
        mutationFn: (id: number) => apis.engine.assignment.assignmentBudgetEntryDeleteDelete({ id }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.BudgetSearch] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.BudgetTotals] });

            toast.success("Entry deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete entry"),
    });

    return {
        entryDetails: entryDetailsQuery,
        entriesSearchList: entriesSearchListQuery,
        entryTotals: entryTotalsQuery,

        saveEntry: saveEntryMutation,
        deleteEntry: deleteEntryMutation,
    };
}
